<script lang="ts">
  // Eine Frage beliebigen Typs anzeigen und beantworten (Multiple Choice, Schätzen, Reihenfolge, Zahlenschloss).
  import { onMount } from 'svelte';
  import type { Question } from '../../shared/types.ts';
  import { check, shuffleMc, shuffleNotIdentity, solutionText, type Answer } from '../../shared/quiz.ts';
  import { t } from '../i18n.ts';
  import { audio } from '../audio/audio.ts';
  import QuizImage from './quiz/QuizImage.svelte';
  import QuizAudio from './quiz/QuizAudio.svelte';
  import ReportButton from './quiz/ReportButton.svelte';
  import { recordAnswer } from '../api/content.ts';
  import { feature } from '../../shared/features.ts';
  import { logAnswer } from '../game/profile.ts';
  import { haptic } from '../pwa.ts';
  import type { LogEntry } from '../../shared/meta.ts';

  import type { Snippet } from 'svelte';
  let {
    q,
    onAnswered,
    children,
    hideFeedback = false,
    hint = 0,
    solvedByHint = false,
    keepOrder = false,
    mode = 'adventure',
    hideIdx = [],
  }: { q: Question; onAnswered: (right: boolean, a: Answer) => void; children?: Snippet; hideFeedback?: boolean; hint?: number; solvedByHint?: boolean; keepOrder?: boolean; mode?: LogEntry['mode'] | null; hideIdx?: number[] } = $props();

  let done = $state(false);
  let right = $state(false);
  let root: HTMLDivElement | undefined = $state();

  // MC
  let order = $state<number[]>([]);
  let picked = $state<number | null>(null);
  // Schätzen
  let est = $state(0);
  // Reihenfolge (Liste von Original-Indizes)
  let seq = $state<number[]>([]);
  let grabbed = $state<number | null>(null);
  // Zahlenschloss
  let digits = $state<number[]>([]);
  // Zuordnung: gewählter rechter Originalindex je linkem Eintrag (-1 = leer); rechte Optionen gemischt
  let matchSel = $state<number[]>([]);
  let rightOrder = $state<number[]>([]);
  // Lückentext
  let clozeSel = $state<string[]>([]);
  let clozeOpts = $state<string[]>([]);

  $effect(() => {
    if (solvedByHint && !done) {
      done = true;
      right = true;
    }
  });
  const isMcLike = $derived(q.type === 'mc' || q.type === 'image' || q.type === 'audio');
  /** per Hinweis ausgeblendete falsche Optionen (Originalindizes) */
  const hiddenOpts = $derived.by(() => {
    if (!(q.type === 'mc' || q.type === 'image' || q.type === 'audio')) return new Set<number>();
    const wrong = order.filter((i) => i !== q.answer);
    // Online-Joker 50:50: vom Server ausgeblendete Optionen
    return new Set([...wrong.slice(0, Math.min(2, hint)), ...hideIdx]);
  });
  const estHint = $derived.by(() => {
    if (q.type !== 'estimate' || hint < 1) return '';
    const f = hint >= 2 ? 0.2 : 0.5;
    const lo = q.answer * (1 - f), hi = q.answer * (1 + f);
    return t('quiz.hintRange', { lo: fmt(Math.round(lo)), hi: fmt(Math.round(hi)), unit: q.unit ?? '' });
  });
  $effect(() => {
    // Hinweise für Reihenfolge/Zahlenschloss/Zuordnung/Lückentext: die ersten k Teile vorgeben
    const k = Math.min(hint, 2);
    if (done || k === 0) return;
    if (q.type === 'order') {
      const rest = seq.filter((v) => v >= k);
      seq = [...Array.from({ length: k }, (_, i) => i), ...rest];
    } else if (q.type === 'numlock') {
      digits = digits.map((d, i) => (i < k ? Number(q.answer[i]) : d));
    } else if (q.type === 'match') {
      matchSel = matchSel.map((v, i) => (i < k ? i : v));
    } else if (q.type === 'cloze') {
      clozeSel = clozeSel.map((v, i) => (i < k ? q.gaps[i] : v));
    }
  });

  onMount(() => {
    if (q.type === 'mc') order = keepOrder ? q.options.map((_, i) => i) : shuffleMc(q.options.length);
    if (q.type === 'estimate') {
      const [lo, hi] = q.range ?? [0, q.answer * 2 || 100];
      est = roundNice(lo + (hi - lo) / 2, lo, hi);
    }
    if (q.type === 'order') seq = shuffleNotIdentity(q.items.length);
    if (q.type === 'numlock') digits = Array(q.digits).fill(0);
    if (q.type === 'image' || q.type === 'audio') order = keepOrder ? q.options.map((_, i) => i) : shuffleMc(q.options.length);
    if (q.type === 'match') {
      matchSel = q.pairs.map(() => -1);
      rightOrder = shuffleMc(q.pairs.length);
    }
    if (q.type === 'cloze') {
      clozeSel = q.gaps.map(() => '');
      {
        const words = [...q.gaps, ...q.distractors].filter((w, i, a) => w && a.indexOf(w) === i);
        clozeOpts = shuffleMc(words.length).map((i) => words[i]);
      }
    }
    // neue Frage: Fokus auf den Fragetext, damit Screenreader ihn zuerst vorlesen (Antworten per Tab oder Taste 1–4/A–D)
    if (matchMedia('(pointer: fine)').matches) setTimeout(() => root?.querySelector<HTMLElement>('.prompt')?.focus({ preventScroll: true }), 30);
  });

  function roundNice(v: number, lo: number, hi: number) {
    const step = stepOf(lo, hi);
    return Math.round(v / step) * step;
  }
  function stepOf(lo: number, hi: number) {
    const span = hi - lo;
    if (span <= 20) return span <= 2 ? 0.1 : 1;
    const p = Math.pow(10, Math.floor(Math.log10(span / 100)));
    return Math.max(1, p);
  }

  function submit(a: Answer) {
    if (done) return;
    done = true;
    right = check(q, a);
    if (!hideFeedback) {
      audio.sfx(right ? 'right' : 'wrong');
      haptic(right ? 30 : [70, 50, 70]);
      if (feature.contentEngine && !q.id.startsWith('ki-')) recordAnswer(q.id, right);
    }
    if (mode && !solvedByHint) logAnswer(q, right, mode);
    onAnswered(right, a);
  }

  function chooseMc(di: number) {
    picked = di;
    submit({ mc: order[di] });
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= seq.length) return;
    const s = [...seq];
    [s[i], s[j]] = [s[j], s[i]];
    seq = s;
  }
  function tapOrder(i: number) {
    if (grabbed === null) grabbed = i;
    else {
      const s = [...seq];
      [s[grabbed], s[i]] = [s[i], s[grabbed]];
      seq = s;
      grabbed = null;
    }
  }
  function spin(i: number, d: number) {
    const ds = [...digits];
    ds[i] = (ds[i] + d + 10) % 10;
    digits = ds;
  }

  function onKey(e: KeyboardEvent) {
    if (done) return;
    const el = e.target as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
    if (!isMcLike || e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key.toLowerCase();
    const i = /^[1-4]$/.test(k) ? Number(k) - 1 : 'abcd'.indexOf(k);
    if (i >= 0 && i < order.length && !hiddenOpts.has(order[i])) chooseMc(i);
  }

  const fmt = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
</script>

<svelte:window onkeydown={onKey} />

<div class="card" bind:this={root} data-qtype={q.type}>
  <p class="cat">{q.category}{#if (q as any).aiGenerated}<span class="ki" title={t('quiz.kiHint')} data-testid="ki-badge">KI-Frage</span>{/if}</p>
  <p class="prompt" id="qprompt" tabindex="-1" data-testid="quiz-prompt" data-qid={q.id}>{q.prompt}</p>

  {#if q.type === 'image'}
    <QuizImage spec={q.image} />
  {:else if q.type === 'audio'}
    <QuizAudio spec={q.audio} />
  {/if}

  {#if isMcLike && (q.type === 'mc' || q.type === 'image' || q.type === 'audio')}
    <div class="opts" role="group" aria-label={t('quiz.options')}>
      {#each order as oi, di}
        <button
          class="opt"
          class:right={done && !hideFeedback && oi === q.answer}
          class:wrong={!hideFeedback && picked === di && oi !== q.answer}
          class:picked={hideFeedback && picked === di}
          class:gone={hiddenOpts.has(oi)}
          disabled={done || hiddenOpts.has(oi)}
          onclick={() => chooseMc(di)}
          data-testid={'opt-' + di}
        >
          <span class="key">{'ABCD'[di]}</span>
          <span>{q.options[oi]}</span>
        </button>
      {/each}
    </div>
  {:else if q.type === 'estimate'}
    {@const lo = q.range?.[0] ?? 0}
    {@const hi = q.range?.[1] ?? (q.answer * 2 || 100)}
    <div class="estimate">
      {#if estHint}<p class="hintline" data-testid="hint-line">{estHint}</p>{/if}
      <output class="val" data-testid="est-value">{fmt(est)} <small>{q.unit ?? ''}</small></output>
      <input
        type="range"
        min={lo}
        max={hi}
        step={stepOf(lo, hi)}
        bind:value={est}
        disabled={done}
        aria-label={t('quiz.estimate')}
        data-testid="est-range"
      />
      <div class="row">
        <input class="num" type="number" bind:value={est} disabled={done} aria-label={t('quiz.estimateExact')} data-testid="est-input" />
        <button class="btn primary" disabled={done} onclick={() => submit({ estimate: Number(est) })} data-testid="quiz-submit">{t('quiz.lockIn')}</button>
      </div>
    </div>
  {:else if q.type === 'order'}
    <p class="hint">{q.orderHint ?? t('quiz.orderHint')}</p>
    <ol class="order" aria-label={t('quiz.orderList')}>
      {#each seq as oi, i (oi)}
        <li class:grab={grabbed === i} class:right={done && !hideFeedback && oi === i} class:wrong={done && !hideFeedback && oi !== i}>
          <button class="item" disabled={done} onclick={() => tapOrder(i)} data-testid={'ord-' + i}>{q.items[oi]}</button>
          <span class="arrows">
            <button class="mini" disabled={done || i === 0} onclick={() => move(i, -1)} aria-label={t('quiz.itemUp', { item: q.items[oi], pos: i + 1 })}>▲</button>
            <button class="mini" disabled={done || i === seq.length - 1} onclick={() => move(i, 1)} aria-label={t('quiz.itemDown', { item: q.items[oi], pos: i + 1 })}>▼</button>
          </span>
        </li>
      {/each}
    </ol>
    <button class="btn primary submit" disabled={done} onclick={() => submit({ order: [...seq] })} data-testid="quiz-submit">{t('quiz.lockIn')}</button>
  {:else if q.type === 'match'}
    <div class="match" role="group" aria-label={t('quiz.match')}>
      {#each q.pairs as pair, i}
        <label class="mrow" class:right={done && !hideFeedback && matchSel[i] === i} class:wrong={done && !hideFeedback && matchSel[i] !== i}>
          <span class="left">{pair[0]}</span>
          <select bind:value={matchSel[i]} disabled={done || i < Math.min(hint, 2)} data-testid={'match-' + i}>
            <option value={-1}>{t('quiz.choose')}</option>
            {#each rightOrder as ri}<option value={ri}>{q.pairs[ri][1]}</option>{/each}
          </select>
        </label>
      {/each}
    </div>
    <button class="btn primary submit" disabled={done || matchSel.some((v) => v < 0)} onclick={() => submit({ match: [...matchSel] })} data-testid="quiz-submit">{t('quiz.lockIn')}</button>
  {:else if q.type === 'cloze'}
    {@const parts = q.text.split(/(\{\d\})/)}
    <p class="cloze">
      {#each parts as part}
        {#if /^\{\d\}$/.test(part)}
          {@const gi = Number(part.slice(1, -1))}
          <select bind:value={clozeSel[gi]} disabled={done || gi < Math.min(hint, 2)} aria-label={t('quiz.gap', { n: gi + 1 })} data-testid={'gap-' + gi}>
            <option value="">…</option>
            {#each clozeOpts as o}<option value={o}>{o}</option>{/each}
          </select>
        {:else}{part}{/if}
      {/each}
    </p>
    <button class="btn primary submit" disabled={done || clozeSel.some((v) => !v)} onclick={() => submit({ cloze: [...clozeSel] })} data-testid="quiz-submit">{t('quiz.lockIn')}</button>
  {:else if q.type === 'numlock'}
    <div class="lockwheels" role="group" aria-label={t('quiz.numlock')}>
      {#each digits as d, i}
        <div class="wheel" role="group" aria-label={t('quiz.digitN', { n: i + 1, d })}>
          <button class="mini" disabled={done} onclick={() => spin(i, 1)} aria-label={t('quiz.digitUp', { n: i + 1, d })} data-testid={'nl-up-' + i}>▲</button>
          <span class="digit" aria-live="polite" data-testid={'nl-' + i}>{d}</span>
          <button class="mini" disabled={done} onclick={() => spin(i, -1)} aria-label={t('quiz.digitDown', { n: i + 1, d })}>▼</button>
        </div>
      {/each}
    </div>
    <button class="btn primary submit" disabled={done} onclick={() => submit({ numlock: digits.join('') })} data-testid="quiz-submit">{t('quiz.tryCode')}</button>
  {/if}

  {#if done && hideFeedback}
    <div class="feedback locked" role="status" aria-live="polite" data-testid="quiz-locked">
      <strong>{t('quiz.lockedIn')}</strong>
      {@render children?.()}
    </div>
  {:else if done}
    <div class="feedback" class:good={right} role="status" aria-live="assertive" data-testid="quiz-feedback">
      <strong>{solvedByHint ? t('quiz.solvedByHint') : right ? t('quiz.right') : t('quiz.wrong')}</strong>
      {#if q.type !== 'mc' || solvedByHint}<span class="sol">{t('quiz.solution')}: <b>{solutionText(q)}</b></span>{/if}
      <p>{q.explanation}</p>
      <a href={q.source.url} target="_blank" rel="noopener" class="src">{t('quiz.source')}: {q.source.title}</a>
      {#if feature.contentEngine}<ReportButton qid={q.id} />{/if}
      {@render children?.()}
    </div>
  {/if}
</div>

<style>
  .cat {
    margin: 10px 0 0;
    text-align: center;
    font-family: var(--font-hand);
    font-size: 1.3rem;
    color: var(--amber2);
  }
  .ki {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 99px;
    background: var(--sea1);
    color: var(--white);
    font-family: var(--font-body);
    font-size: 0.75rem;
    vertical-align: middle;
  }
  .prompt {
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
    margin: 4px 0 16px;
  }
  .opts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  @media (max-width: 480px) {
    .opts {
      grid-template-columns: 1fr;
    }
  }
  .opt {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 54px;
    padding: 10px 12px;
    text-align: left;
    border-radius: 12px;
    border: 1.5px solid rgba(226, 194, 122, 0.35);
    background: rgba(12, 20, 34, 0.55);
    transition: all 0.2s;
  }
  .opt:hover:not(:disabled) {
    border-color: var(--amber2);
    background: rgba(58, 85, 120, 0.6);
  }
  .opt:focus-visible,
  .item:focus-visible,
  .mini:focus-visible {
    outline: 3px solid var(--amber2);
  }
  .opt:disabled {
    cursor: default;
    opacity: 0.75;
  }
  .opt.right,
  .order li.right {
    opacity: 1;
    border-color: var(--ok);
    background: var(--ok-bg);
  }
  .opt.wrong,
  .order li.wrong {
    opacity: 1;
    border-color: var(--bad);
    background: var(--bad-bg);
  }
  .key {
    display: inline-grid;
    place-items: center;
    width: 28px;
    height: 28px;
    flex: none;
    border-radius: 50%;
    background: var(--brass1);
    color: var(--ink);
    font-weight: 800;
    font-size: 0.85rem;
  }
  .estimate {
    display: grid;
    gap: 12px;
  }
  .val {
    text-align: center;
    font-family: var(--font-display);
    font-size: 2.2rem;
    color: var(--amber3);
  }
  .val small {
    font-size: 1rem;
    color: var(--paper);
  }
  input[type='range'] {
    width: 100%;
    min-width: 0;
    margin: 0;
    accent-color: var(--amber1);
    height: 44px;
  }
  .row {
    display: flex;
    gap: 10px;
  }
  .num {
    flex: 1;
    min-width: 0;
    min-height: 48px;
    border-radius: 12px;
    border: 1.5px solid var(--panel-edge);
    background: rgba(12, 20, 34, 0.6);
    color: var(--white);
    font: inherit;
    font-size: 1.1rem;
    padding: 0 12px;
  }
  .hint {
    text-align: center;
    margin: -8px 0 10px;
    color: var(--paper);
    opacity: 0.85;
  }
  .order {
    list-style: none;
    padding: 0;
    margin: 0 0 12px;
    display: grid;
    gap: 8px;
    counter-reset: o;
  }
  .order li {
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 12px;
    border: 1.5px solid rgba(226, 194, 122, 0.35);
    background: rgba(12, 20, 34, 0.55);
    padding: 4px 6px 4px 12px;
    counter-increment: o;
  }
  .order li::before {
    content: counter(o) '.';
    font-weight: 800;
    color: var(--amber2);
  }
  .order li.grab {
    border-color: var(--amber2);
    box-shadow: 0 0 0 2px rgba(246, 200, 109, 0.4);
  }
  .item {
    flex: 1;
    text-align: left;
    background: none;
    border: 0;
    min-height: 44px;
    padding: 0 4px;
  }
  .arrows {
    display: flex;
    gap: 4px;
  }
  .mini {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    border: 1px solid var(--panel-edge);
    background: rgba(58, 85, 120, 0.6);
  }
  .mini:disabled {
    opacity: 0.35;
  }
  .submit {
    width: 100%;
  }
  .lockwheels {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 14px;
  }
  .wheel {
    display: grid;
    gap: 6px;
    justify-items: center;
  }
  .digit {
    width: 56px;
    height: 72px;
    display: grid;
    place-items: center;
    font-family: var(--font-display);
    font-size: 2.4rem;
    color: var(--ink);
    background: linear-gradient(180deg, #b08a3e, #e2c27a 45%, #b08a3e);
    border-radius: 10px;
    box-shadow: inset 0 -6px 10px rgba(0, 0, 0, 0.35), inset 0 6px 10px rgba(0, 0, 0, 0.35);
  }
  .feedback {
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--bad-bg);
    border: 1px solid var(--bad);
    display: grid;
    gap: 6px;
  }
  .opt.gone {
    opacity: 0.25;
    text-decoration: line-through;
  }
  .hintline {
    margin: 0;
    text-align: center;
    color: var(--amber2);
  }
  .match {
    display: grid;
    gap: 8px;
    margin-bottom: 12px;
  }
  .mrow {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    align-items: center;
    padding: 6px 8px;
    border-radius: 12px;
    border: 1.5px solid rgba(226, 194, 122, 0.35);
    background: rgba(12, 20, 34, 0.55);
  }
  .mrow.right {
    border-color: var(--ok);
    background: var(--ok-bg);
  }
  .mrow.wrong {
    border-color: var(--bad);
    background: var(--bad-bg);
  }
  .left {
    font-weight: 700;
  }
  select {
    min-height: 44px;
    min-width: 0;
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid var(--panel-edge);
    background: var(--night1);
    color: var(--white);
    font: inherit;
    padding: 0 8px;
  }
  .cloze {
    line-height: 2.6;
    font-size: 1.1rem;
    text-align: center;
  }
  .cloze select {
    width: auto;
    display: inline-block;
    margin: 0 4px;
  }
  .feedback.locked {
    background: rgba(246, 200, 109, 0.14);
    border-color: var(--amber1);
  }
  .opt.picked {
    opacity: 1;
    border-color: var(--amber2);
    background: rgba(246, 200, 109, 0.25);
  }
  .feedback.good {
    background: var(--ok-bg);
    border-color: var(--ok);
  }
  .feedback p {
    margin: 0;
    line-height: 1.45;
  }
  .sol b {
    color: var(--amber3);
  }
  .src {
    color: var(--amber2);
    font-size: 0.85rem;
  }
  .prompt:focus {
    outline: none;
  }
</style>
